/**
 * @typedef {Object} RouteData
 * @property {[number, number]} charge - Starting charge and ending charge in percentage.
 * @property {Array.<RouteSegment>} segments - Array of segments.
 */

/**
 * @typedef {Object} RouteSegment
 * @property {Coordinates} start - Starting coordinates of the segment.
 * @property {Coordinates} end - Ending coordinates of the segment.
 * @property {number} duration - Duration of the segment in seconds.
 */

/**
 * @typedef {Array.<number>} Coordinates
 * @property {number} 0 - Longitude of the coordinate.
 * @property {number} 1 - Latitude of the coordinate.
 */

/**
 * @typedef {Object} VehicleElement
 * @property {string} type - The type of the vehicle (e.g, 'truck', 'truck-ghost').
 * @property {number} index - The registration number of the vehicle.
 * @property {Object} marker - The marker for the vehicle on the map.
 * @property {Object} element - The DOM element for the vehicle.
 */

/**
 * @typedef {Object} Vehicle
 * @property {string} id - The ID of the vehicle.
 * @property {string} source - The source of the vehicle.
 * @property {number} travelTime - How long the vehicle has been on the segment.
 * @property {number} ghost - The ghost progress of the vehicle along the route (0-100).
 * @property {string} [status] - The status of the vehicle (e.g, 'active', 'charging', 'error').
 */

function calculateBearing(point1, point2) {
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const toDegrees = (radians) => radians * (180 / Math.PI);

  const latitude1 = toRadians(point1[1]);
  const latitude2 = toRadians(point2[1]);
  const deltaLongitude = toRadians(point2[0] - point1[0]);

  const y = Math.sin(deltaLongitude) * Math.cos(latitude2);
  const x = Math.cos(latitude1) * Math.sin(latitude2) - Math.sin(latitude1) * Math.cos(latitude2) * Math.cos(deltaLongitude);
  const theta = Math.atan2(y, x);

  return (toDegrees(theta) + 180) % 360; // Normalize to 0-360 degrees
}

function createElementWithAttributes(tag, attributes) {
  const element = document.createElement(tag);
  for (const key in attributes) {
    if (attributes.hasOwnProperty(key)) {
      element.setAttribute(key, attributes[key]);
    }
  }
  return element;
}

/**
 * Class to represent a vehicle with a ghost vehicle
 * @class VehicleWithGhost
 * @property {Array.<RouteSegment>} segment - The route data for the vehicle.
 * @property {Array.<VehicleElement>} vehicles - The vehicle and ghost vehicle objects.
 */
class VehicleWithGhost {
  /**
   * Creates an instance of VehicleWithGhost.
   * @param {Vehicle} vehicle - The vehicle data.
   * @param {Array.<RouteSegment>} segments - The route data for the vehicle.
   */
  constructor(vehicle, segments) {
    const vehicleElement = createElementWithAttributes('map-marker', {
      type: 'truck',
      regnumber: vehicle.id,
      progress: 0,
      status: vehicle.status ?? 'active',
      route: vehicle.source,
      soc: 100,
      distance: 0,
      rdt: 0,
      angle: 0,
    });

    const ghostElement = createElementWithAttributes('map-marker', {
      type: 'truck-ghost',
      regnumber: vehicle.id,
      progress: 0,
      status: 'ghost',
      route: vehicle.source,
      soc: 100,
      distance: 0,
      rdt: 0,
      angle: 0,
    });

    // this.data = vehicle;
    this.segments = segments;
    this.vehicles = [
      {
        type: 'truck',
        index: Math.round((vehicle.progress * this.segments.length) / 100),
        marker: null,
        element: vehicleElement,
        travelTime: 0,
      },
    ];

    if (vehicle.ghost > 0) {
      this.vehicles.push({
        type: 'truck-ghost',
        index: Math.round(((vehicle.progress + vehicle.ghost) * this.segments.length) / 100),
        marker: null,
        element: ghostElement,
        travelTime: 0,
      });
    }
  }

  /**
   * Updates the positions of the vehicle markers on the map.
   * This method increments the vehicle's segment index to update the marker position
   * @param {*} map The map instance to update the vehicle markers on.
   */
  update(map) {
    for (let vehicle of this.vehicles) {
      this.updateVehicle(map, vehicle);
    }
  }

  updateVehicle(map, vehicle) {
    let segment = this.segments[vehicle.index];
    if (!vehicle.marker) {
      vehicle.marker = new mapboxgl.Marker(vehicle.element).setLngLat(segment.start).addTo(map);
    }

    if (vehicle.element.getAttribute('status') != 'charging') {
      vehicle.index = (vehicle.index + 1) % this.segments.length;
      vehicle.travelTime = 0; // Reset travel time when moving to the next segment
      vehicle.progress = 0; // Reset progress when moving to the next segment
      segment = this.segments[vehicle.index];
      vehicle.marker.setLngLat(segment.start);
    }

    // Update vehicle attributes
    vehicle.element.setAttribute('distance', 80);
    vehicle.element.setAttribute('rdt', (vehicle.index / this.segments.length) * 100);
    vehicle.element.setAttribute('angle', calculateBearing(segment.start, segment.end));
    vehicle.element.setAttribute('soc', 100 - (vehicle.index / this.segments.length) * 100);
    vehicle.element.setAttribute('progress', (vehicle.index / this.segments.length) * 100);
  }

  /**
   * @param {number} delta - The time delta (in ms) for the animation, used to calculate progress.
   */
  animate(delta = 0) {
    for (let item of this.vehicles) {
      if (!item.marker) {
        continue;
      }
      if (item.element.getAttribute('status') == 'charging') {
        continue;
      }
      const segment = this.segments[item.index];

      // Update travel time
      item.travelTime = (item.travelTime || 0) + delta;
      const progress = item.travelTime / (this.segments[item.index].duration * 1000); // Convert to percentage
      item.progress = progress * 100; // Store progress as a percentage

      // console.log('Animating vehicle', {
      //   item,
      //   segment,
      //   progress,
      // });

      // Interpolate between current and next point
      const lng = segment.start[0] + (segment.end[0] - segment.start[0]) * progress;
      const lat = segment.start[1] + (segment.end[1] - segment.start[1]) * progress;
      item.marker.setLngLat([lng, lat]);
    }
  }
}

/**
 * Class to manage ghost vehicles on the map.
 * @class GhostVehicles
 * @property {boolean} animating - Whether the ghost vehicles are currently animating.
 * @property {Object|null} parent - Reference to the map web component, or null if not set.
 * @property {Object|null} map - Reference to the map instance, or null if not set.
 * @property {Array.<VehicleWithGhost>} ghosts - Array of ghost vehicles.
 */
export default class GhostVehicles {
  /**
   * Creates an instance of GhostVehicles.
   * @param {{ map: Object|null }} parent - The parent map web component, containing the map instance.
   */
  constructor(parent) {
    /**
     * Whether the ghost vehicles are currently animating.
     * @type {boolean}
     */
    this.animating = false;

    /**
     * Reference to the map web component, or null if not set.
     * @type {Object|null}
     */
    this.parent = parent;

    /**
     * Reference to the map instance, or null if not set.
     * @type {Object|null}
     */
    this.map = parent.map;

    /**
     * Array of ghost vehicles.
     * @type {Array.<VehicleWithGhost>}
     */
    this.ghosts = [];
  }

  /**
   * Load vehicles and their routes into the ghosts array.
   * @param {Array.<Vehicle>} vehicles - Array of vehicle objects.
   * @param {Object<string, RouteData>} routes - Object containing route data keyed by source.
   */
  load(vehicles, routes) {
    // Join route legs on source prefix to get full path
    /**
     * @type {Map<string, Array.<RouteSegment>>}
     */
    const segmentMap = new Map();
    for (let source in routes) {
      const prefix = source.split('_')[0];
      if (segmentMap.get(prefix)) {
        segmentMap.set(prefix, segmentMap.get(prefix).concat(routes[source].segments));
      } else {
        segmentMap.set(prefix, routes[source].segments);
      }
    }

    for (let vehicle of vehicles) {
      let segments = segmentMap.get(vehicle.source);
      if (segments) {
        this.ghosts.push(new VehicleWithGhost(vehicle, segments));
      }
    }
  }

  /**
   * Updates the positions of all ghost vehicles on the map.
   */
  update() {
    for (let ghost of this.ghosts) {
      ghost.update(this.map);
    }
  }

  /**
   * Clears all ghost vehicles from the map.
   */
  clear() {
    for (let ghost of this.ghosts) {
      for (let vehicle of ghost.vehicles) {
        if (vehicle.marker) {
          vehicle.marker.remove();
        }
      }
    }
    this.animating = false;
    this.lastTimestamp = 0;
    this.ghosts = [];
  }

  // Principles of this animation
  // 1. Animate between the start and end of each segment, over the duration of the segment.
  // 2. Move to next segment after duration is complete.
  // e.g.
  // 1. animate() // Animate marker position from a to b over duration t
  // 2. update() // Update marker position by "snapping" to the next segment start point
  /**
   * Toggles animation for ghost vehicles or updates their positions based on a timestamp.
   * @param {number} [timestamp=0] - The current timestamp for animation.
   */
  animate(timestamp = 0) {
    const delta = timestamp - (this.lastTimestamp || 0);
    this.lastTimestamp = timestamp || 0;
    if (!timestamp) {
      this.animating = !this.animating;
      this.update();
    }

    if (!this.animating) {
      return;
    }

    // const progress = (elapsed % delay) / delay;
    for (let ghost of this.ghosts) {
      for (let vehicle of ghost.vehicles) {
        if (vehicle.progress >= 100) {
          ghost.updateVehicle(this.map, vehicle);
        }
      }
      ghost.animate(delta);
    }
    requestAnimationFrame(this.animate.bind(this));
  }
}
